<template>
  <a-spin
    :spinning="isLoading"
    class="custom-spinner"
    style="height: 100vh; top: 20%"
  >
    <administrator-project-layout :model="model">
      <template #underInfo>
        <div class="administrator-project__common-progress" v-if="model.id">
          <progress-bar-generated
            :show-label="true"
            :completed="model.completedCount"
            :completed-overdue="model.completedOverdueCount"
            :incomplete-overdue="model.incompleteOverdueCount"
            :incomplete="model.incompleteCount"
          />
        </div>
      </template>
      <div class="project-report">
        <div v-if="model.isMyProject && toApprove">
          <a-form
            class="form administrator-project-form"
            layout="vertical"
            :model="model"
            @validate="errors.onValidate"
            @finish="() => null"
          >
            <h3>Отчет по проекту</h3>
            <a-form-item
              name="approval"
              :help="errors.getError('approval')"
              :validate-status="errors.getStatus('approval')"
            >
              <a-row class="administrator-report">
                <a-col class="administrator-report-title">
                  <h4>Этот проект отправлен на закрытие</h4>
                </a-col>
                <a-col class="administrator-report-info">
                  <p>
                    Один из участников проекта инициировал его закрытие.
                    Пожалуйста, ознакомьтесь с отчетом по результатам выполнения
                    проекта ниже. Если вы согласны закрыть проект, пожалуйста,
                    подтвердите это. Если вы считаете, что проект должен быть
                    продолжен, верните проект в работу.
                  </p>

                  <p>
                    <span>Инициатор закрытия:&nbsp;</span
                    >{{
                      report.closedByFio + ', ' + report.roleText.toLowerCase()
                    }}
                  </p>

                  <p>
                    <span>Основание для закрытия:&nbsp;</span
                    >{{ report.closeReason }}
                  </p>

                  <p>
                    <span>Комментарий:&nbsp;</span>{{ report.closeComment }}
                  </p>
                </a-col>
              </a-row>
            </a-form-item>
          </a-form>

          <a-divider class="report-divider" />
        </div>
        <div>
          <slot>
            <h3>Отчет</h3>
            <h4 class="disabled">Полученный результат</h4>
          </slot>
          <a-textarea
            v-model:value="report.result"
            placeholder="Опишите полученный результат"
            :autoSize="{ minRows: 8, maxRows: 8 }"
            style="padding: 10px"
            disabled
          />
          <div class="attachments">
            <attachments-input
              :init-attachments="report.attachments"
              :is-editable="false"
            />
          </div>
        </div>

        <div v-if="store.getters.enableCompetencies">
          <a-divider class="report-divider" />

          <administrator-report-competencies
            ref="reportCompetenciesRef"
            :project-status-id="model.projectStatusId"
            :is-my-project="model.isMyProject"
            :to-approve="toApprove"
            @saved="onCompetenciesSaved"
          />
        </div>

        <div
          v-if="model.isMyProject && toApprove"
          class="administrator-report-buttons"
        >
          <div v-if="model.approval">
            <c-button-danger @click="() => (showForm = true)">
              Вернуть в работу
            </c-button-danger>
          </div>
          <div>
            <c-button-primary @click="handleOnSubmit" :loading="isLoading">
              Закрыть проект
            </c-button-primary>
          </div>
        </div>
      </div>
      <c-drawer v-model:open="showForm" destroyOnClose>
        <component
          :is="AdministratorReportRejectForm"
          :project-approval-id="model.approval.id"
          @close="() => (showForm = !showForm)"
          @update="updateOnReject"
        />
      </c-drawer>
    </administrator-project-layout>
  </a-spin>
</template>

<script setup>
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useApi } from '@/api/use-api';
  import AdministratorProjectLayout from '../components/AdministratorProjectLayout.vue';
  import AttachmentsInput from '@/components/attachments/AttachmentsInput.vue';
  import CButtonPrimary from '@/components/UI/CButtonPrimary';
  import CButtonDanger from '@/components/UI/CButtonDanger';
  import CDrawer from '@/components/UI/CDrawer';
  import AdministratorReportRejectForm from './AdministratorReportRejectForm';
  import ErrorsHandler from '@/components/form/errors-handler';
  import ProgressBarGenerated from '@/components/projects/portfolio/components/ProgressBarGenerated.vue';
  import AdministratorReportCompetencies from '@/views/administrator/projects/report/AdministratorReportCompetencies.vue';
  import { useStore } from 'vuex';
  import { DOCUMENT_APPROVAL_STATUS_TO_APPROVE } from '@/api/models/DocumentApprovalStatus';
  import { PROJECT_STATUS_ACCEPTANCE } from '@/api/models/ProjectStatus';

  const api = useApi();
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const emit = defineEmits(['update']); // Parent update
  const reportCompetenciesRef = ref(null);

  const model = reactive({
    id: null,
    number: '',
    name: 'Новый проект',
    description: '',
    projectType: '',
    requestId: null,
    status: {
      type: 'draft',
      text: 'Черновик',
    },
    projectStatusId: null,
    isMyProject: false,
    completedCount: 0,
    completedOverdueCount: 0,
    incompleteOverdueCount: 0,
    incompleteCount: 0,
    approval: null,
  });
  const report = reactive({
    closedByFio: '',
    roleText: '',
    closeReason: '',
    closeComment: '',
    result: '',
    attachments: [],
  });
  const isLoading = ref(true);
  const showForm = ref(false);

  const toApprove = computed(() => {
    return (
      model.projectStatusId === PROJECT_STATUS_ACCEPTANCE &&
      model.status?.type === DOCUMENT_APPROVAL_STATUS_TO_APPROVE
    );
  });

  const errors = new ErrorsHandler({
    approval: '',
  });

  onMounted(async () => {
    await getModel();
    await getReport();
    isLoading.value = false;
  });

  const getReport = async () => {
    try {
      const response = await api.projectReport.view(route.params.id);
      report.closedByFio = response.report.closedByFio;
      report.roleText = response.report.roleText;
      report.closeReason = response.report.closeReason;
      report.closeComment = response.report.closeComment;
      report.result = response.report.result;
      report.attachments = response.report.attachments;
    } catch (error) {
      console.error('error', error);
    }
  };

  const getModel = async () => {
    try {
      const response = await api.administratorPage.getProject(route.params.id);
      model.name = response.project.name;
      model.number = response.project.id;
      model.id = response.project.id;
      model.type = response.project.type;
      model.requestId = response.project.requestId;
      model.status = response.project.status;
      model.projectStatusId = response.project.projectStatusId;
      model.isMyProject = response.project.isMyProject;
      model.completedCount = response.project.completedCount;
      model.completedOverdueCount = response.project.completedOverdueCount;
      model.incompleteOverdueCount = response.project.incompleteOverdueCount;
      model.incompleteCount = response.project.incompleteCount;
      model.approval = response.project.approval;
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleOnSubmit = async () => {
    isLoading.value = true;
    if (store.getters.enableCompetencies) {
      await reportCompetenciesRef.value.saveCompetencies();
    } else {
      await handleSaveReport();
    }
  };

  const onCompetenciesSaved = async (result) => {
    if (result) {
      await handleSaveReport();
    } else {
      isLoading.value = false;
    }
  };

  const handleSaveReport = async () => {
    if (model.approval) {
      try {
        errors.clearErrors();
        await api.administratorPage.approveProject({
          projectApprovalId: model.approval.id,
        });
        emit('update');
      } catch (error) {
        errors.handleApiErrors(error);
      } finally {
        isLoading.value = false;
      }
    }

    await router.push({
      path: '/administrator/project/' + route.params.id + '/agreement',
    });
  };

  const updateOnReject = async () => {
    emit('update');
    await router.push(`/administrator/project/` + model.id + `/form`);
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors.scss' as *;

  * {
    margin: 0;
    padding: 0;
  }

  .ant-input {
    font-family: 'Nunito Sans', sans-serif;
    &[disabled] {
      color: $color-text-primary;
    }
  }

  .administrator-project__common-progress {
    margin-top: 42px;
  }

  .project-report {
    margin-top: 10px;
    max-width: 902px;
    padding-bottom: 100px;

    h3 {
      font-size: 18px;
      margin-bottom: 10px;
    }

    h4 {
      font-size: 14px;
      margin: 20px 0 6px;

      &.disabled {
        color: $color-bg-status-new !important;
      }
    }

    .description {
      font-family: 'Nunito Sans', sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      max-width: 678px;
    }

    .report-divider {
      margin: 40px 0;
    }

    .administrator-report {
      background-color: $color-bg-secondary-grey;
      margin-top: 30px;
      min-height: 207px;
      padding: 20px;

      .administrator-report-title {
        h4 {
          font-size: 16px;
          font-weight: 700;
          line-height: 22px;
          margin: 0;
        }
      }

      .administrator-report-info {
        color: $color-bg-status-new;
        display: flex;
        flex-direction: column;
        gap: 18px;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        margin-top: 20px;
        width: 785px;

        span {
          font-weight: 700;
        }
      }
    }

    .competencies-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 20px;

      :deep(.tag) {
        cursor: pointer;
        pointer-events: all;
      }
    }

    .administrator-report-buttons {
      display: flex;
      justify-content: end;
      margin-top: 40px;
      gap: 10px;
    }

    .attachments {
      margin-top: 14px;
    }

    .actions {
      text-align: right;
      margin-top: 26px;
    }
  }
</style>
